import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, EmailOutlined, LockOutlined, ArrowForward, ForumOutlined, GroupOutlined, AssessmentOutlined, SecurityOutlined } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { Link as RouterLink } from "react-router-dom";
import "./loginThemes.css";

export const LOGIN_THEMES = [
  { id: "1", name: "Moderno e Profissional", detail: "Painel azul e acesso em destaque" },
  { id: "2", name: "Com Imagem e Ilustração", detail: "Área de apresentação e cartão" },
  { id: "3", name: "Minimalista e Elegante", detail: "Fundo claro com formas suaves" },
  { id: "4", name: "Tela Dividida Escura", detail: "Apresentação noturna e contraste" },
  { id: "5", name: "Com Fundo de Imagem", detail: "Cartão translúcido e cenário" },
  { id: "6", name: "Compacto Centralizado", detail: "Acesso central e fundo discreto" },
  { id: "7", name: "Painel Azul Premium", detail: "Azul intenso e apresentação premium" },
  { id: "8", name: "Minimalista Claro", detail: "Layout arejado em duas áreas" },
  { id: "9", name: "Glassmorphism Elegante", detail: "Cartão em vidro com fundo escuro" },
  { id: "10", name: "Compacto Moderno", detail: "Formulário central e formas azuis" }
];

const useLogoStyles = makeStyles((theme) => ({
  logo: {
    display: "block", width: "100%", height: "auto", maxHeight: 75, objectFit: "contain",
    content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")"
  }
}));

const DisplayLogo = ({ hero = false }) => {
  const classes = useLogoStyles();
  return <div className={hero ? "lt-brand lt-brand-hero" : "lt-brand"}><img alt="Logo do sistema" className={classes.logo} /></div>;
};

const Benefits = () => (
  <ul className="lt-benefits">
    <li><ForumOutlined fontSize="small" /> Atendimentos mais rápidos</li>
    <li><GroupOutlined fontSize="small" /> Equipe organizada</li>
    <li><AssessmentOutlined fontSize="small" /> Mais resultados</li>
    <li><SecurityOutlined fontSize="small" /> Acesso seguro e confiável</li>
  </ul>
);

// Ilustração produzida com elementos da própria interface, sem conteúdo remoto.
const DashboardIllustration = () => (
  <div className="lt-device" aria-hidden="true">
    <div className="lt-device-top"><span /><span /><span /></div>
    <div className="lt-device-body">
      <div className="lt-device-sidebar"><b /><i /><i /><i /><i /></div>
      <div className="lt-device-content"><strong /><span /><div className="lt-device-rows"><i /><i /><i /></div><div className="lt-device-graph"><em /><em /><em /><em /><em /></div></div>
    </div>
  </div>
);

const Hero = ({ themeId }) => (
  <aside className="lt-hero">
    <div className="lt-hero-top"><DisplayLogo hero /></div>
    <div className="lt-hero-copy">
      <h1>{themeId === "4" || themeId === "9" ? "Atenda, organize e cresça." : themeId === "2" || themeId === "8" ? "Conecte sua equipe e atenda melhor." : "Sua comunicação em um só lugar."}</h1>
      <p>Gerencie atendimentos, conexões e sua equipe com mais produtividade.</p>
      <Benefits />
    </div>
    <DashboardIllustration />
    <div className="lt-hero-foot"><SecurityOutlined fontSize="small" /> Sua equipe conectada com segurança</div>
  </aside>
);

const PreviewFields = () => (
  <div className="lt-fields-demo" aria-hidden="true">
    <div><EmailOutlined fontSize="small" /><span>Email *</span></div>
    <div><LockOutlined fontSize="small" /><span>Senha *</span><Visibility fontSize="small" /></div>
    <div className="lt-demo-check"><b /> Lembrar de mim</div>
    <div className="lt-demo-button">ENTRAR <ArrowForward fontSize="small" /></div>
  </div>
);

const RealFields = ({ user, onChange, onSubmit, allowSignup }) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <form className="lt-real-form" onSubmit={onSubmit} noValidate>
      <label className="lt-input-wrap">
        <EmailOutlined fontSize="small" aria-hidden="true" />
        <input autoFocus type="email" name="email" required aria-label={i18n.t("login.form.email")} placeholder={i18n.t("login.form.email") + " *"} value={user.email} onChange={onChange} autoComplete="email" />
      </label>
      <label className="lt-input-wrap">
        <LockOutlined fontSize="small" aria-hidden="true" />
        <input type={showPassword ? "text" : "password"} name="password" required aria-label={i18n.t("login.form.password")} placeholder={i18n.t("login.form.password") + " *"} value={user.password} onChange={onChange} autoComplete="current-password" />
        <button type="button" className="lt-eye" aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</button>
      </label>
      <button className="lt-submit" type="submit">{i18n.t("login.buttons.submit")} <ArrowForward fontSize="small" /></button>
      {allowSignup && <RouterLink className="lt-register" to="/signup">{i18n.t("login.buttons.register")}</RouterLink>}
    </form>
  );
};

/** Reutilizado na página real e na prévia do White Label para evitar divergência visual. */
const LoginTheme = ({ themeId = "1", preview = false, user, onChange, onSubmit, allowSignup = false }) => {
  const id = LOGIN_THEMES.some((theme) => theme.id === String(themeId)) ? String(themeId) : "1";
  return (
    <div className={`login-theme lt-${id} ${preview ? "lt-preview" : "lt-live"}`}>
      <div className="lt-art-background" aria-hidden="true" />
      <div className="lt-page">
        {["1", "2", "4", "7", "8", "9"].includes(id) && <Hero themeId={id} />}
        <section className="lt-login-panel">
          <div className="lt-login-card">
            <DisplayLogo />
            <div className="lt-welcome">
              <h2>{id === "4" ? "Acesse sua conta" : "Bem-vindo!"}</h2>
              <p>Entre na sua conta para acessar o sistema</p>
            </div>
            {preview ? <PreviewFields /> : <RealFields user={user} onChange={onChange} onSubmit={onSubmit} allowSignup={allowSignup} />}
            <div className="lt-secure"><SecurityOutlined fontSize="small" /> Acesso protegido</div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginTheme;
